import React from 'react';
import {
  LocalizedMessage,
  LocalizedStandardDate,
} from 'intl/LocalizedComponents';
import classnames from 'classnames';
import PropTypes from 'prop-types';
// Core components
import Badge from 'components/core/Badge';
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mCustomer from 'models/customer';
// Custom
import {
  listRowSpecsShape,
  customerDataShape,
} from 'constants/PropTypesShapes';
import { customersShowPath } from 'installations/navigation';
import ResourceAsyncStatusIcon from 'components/items/ResourceAsyncStatusIcon';
import {
  EditIconButton,
  DeleteIconButton,
} from 'components/buttons/IconButtons';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';
import AppIcon from 'components/icons/AppIcon';
import EntityIdLabel from 'installations/assets/entities/EntityIdLabel';
import _get from 'lodash/get';
import CustomerOPInfo from './CustomerOPInfo';

const CustomerState = ({ customer }) => {
  const { inside, outside, unknown } = mCustomer.statesInfo(customer);

  return (
    <Tooltip
      content={(
        <div>
          <LocalizedMessage
            id={inside.labelId}
            values={{ value: inside.value }}
          />
          <div className="box-divider-light my-1" />
          <LocalizedMessage
            id={outside.labelId}
            values={{ value: outside.value }}
          />
          <div className="box-divider-light my-1" />
          <LocalizedMessage
            id={unknown.labelId}
            values={{ value: unknown.value }}
          />
        </div>
      )}
    >
      <Badge content={inside.value} backgroundColor={inside.color}>
        <AppIcon id={inside.iconId} defaultColor />
      </Badge>
    </Tooltip>
  );
};

CustomerState.propTypes = {
  customer: customerDataShape.isRequired,
};

const CustomerAntipassback = ({ customer }) => (
  <div className="d-flex align-items-center">
    {customer.restricted_by_antipassback_entrance && (
      <Tooltip content={<LocalizedMessage id="field.restrictedEntrance" />}>
        <AppIcon id="antipassbackEntrance" defaultColor className="mr-2" />
      </Tooltip>
    )}
    {customer.restricted_by_antipassback_exit && (
      <Tooltip content={<LocalizedMessage id="field.restrictedExit" />}>
        <AppIcon id="antipassbackExit" defaultColor />
      </Tooltip>
    )}
  </div>
);

CustomerAntipassback.propTypes = {
  customer: customerDataShape.isRequired,
};

const CustomerRow = React.forwardRef(
  (
    {
      id, antipassbackEnabled, customer, rowSpecs, withEntity, withOneParkBooking, onDelete,
    },
    ref,
  ) => {
    const { id: customerId } = customer;

    // Permissions
    const {
      pInstallation: { customerWrite, configRead, entityRead },
    } = permissions;

    // Handlers
    const handleDelete = () => {
      onDelete(customer);
    };

    return (
      <TableRow
        ref={ref}
        element={customer}
        rowSpecs={rowSpecs}
        actions={[
          <OnlyWith
            key="bEdit"
            permissions={{
              needed: `${customerWrite}&&${configRead}&&${entityRead}`,
              permissionsOrContext: 'installationPermissions',
            }}
          >
            <EditIconButton path={customersShowPath(id, customerId)} />
          </OnlyWith>,
          <OnlyWith
            key="bDelete"
            permissions={{
              needed: customerWrite,
              permissionsOrContext: 'installationPermissions',
            }}
          >
            <DeleteIconButton onClick={handleDelete} />
          </OnlyWith>,
        ]}
      >
        <TableCell
          className={classnames({
            'table-first-details': rowSpecs.withDetails,
          })}
        >
          <TruncatedTooltipText>{customer.lastname}</TruncatedTooltipText>
        </TableCell>
        <TableCell className="hidden-sm-down">
          <TruncatedTooltipText>{customer.firstname}</TruncatedTooltipText>
        </TableCell>
        <TableCell className="hidden-lg-down">
          <TruncatedTooltipText>{customer.email}</TruncatedTooltipText>
        </TableCell>
        <TableCell className="hidden-lg-down">
          <TruncatedTooltipText>{customer.comments}</TruncatedTooltipText>
        </TableCell>
        {withEntity && (
          <TableCell className="hidden-sm-down">
            <EntityIdLabel id={customer.entity_id} />
          </TableCell>
        )}
        {/* ----- OP Info ----- */}
        {withOneParkBooking && (
          <TableCell className="hidden-md-down">
            {_get(customer, 'metadata.booking_usage', null) && <CustomerOPInfo customer={customer} />}
          </TableCell>
        )}
        <TableCell className="hidden-lg-down">
          <TruncatedTooltipText>
            {customer.last_access && <LocalizedStandardDate value={customer.last_access} />}
          </TruncatedTooltipText>
        </TableCell>
        <TableCell className="table-content-1 hidden-md-down" noPadding>
          <CustomerState customer={customer} />
        </TableCell>
        {antipassbackEnabled && (
          <TableCell className="table-content-1 hidden-lg-down" noPadding>
            <CustomerAntipassback customer={customer} />
          </TableCell>
        )}
        <TableCell className="table-content-1 table-content-center" noPadding>
          <div className="d-flex align-items-center">
            <ResourceAsyncStatusIcon resource={customer} idKey="idCustomer" />
          </div>
        </TableCell>
      </TableRow>
    );
  },
);

CustomerRow.displayName = 'CustomerRow';

CustomerRow.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  antipassbackEnabled: PropTypes.bool.isRequired,
  customer: customerDataShape.isRequired,
  rowSpecs: listRowSpecsShape.isRequired,
  withEntity: PropTypes.bool.isRequired,
  withOneParkBooking: PropTypes.bool.isRequired,
  onDelete: PropTypes.func,
};

CustomerRow.defaultProps = {
  onDelete: null,
};

module.exports = CustomerRow;



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/customers/CustomerRow.js